// src/components/Footer.jsx
import React from "react";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaGem,
  FaShieldAlt,
  FaUsers,
  FaBriefcase,
  FaWheelchair,
  FaArrowRight
} from "react-icons/fa";

const Footer = () => {
  // Professional DiamondBank Zimbabwe color palette - Dark Gray/Black with subtle purple accent
  const colors = {
    primary: "#1a1a1a",      // Dark gray/black as primary
    primaryLight: "#2d2d2d",
    primaryDark: "#0d0d0d",
    accent: "#6b21a5",       // Muted purple accent
    accentLight: "#8b5cc9",
    accentDark: "#4c1d6e",
    secondary: "#e5e7eb",    // Light gray
    white: "#ffffff",
    black: "#000000",
    gray: "#6b7280",
    grayLight: "#9ca3af",
    grayDark: "#374151",
    lightBg: "#f8f9fa"
  };

  const socialLinks = [
    { icon: <FaFacebook />, color: "#1877F2", url: "https://facebook.com/diamondbank" },
    { icon: <FaTwitter />, color: "#1DA1F2", url: "https://twitter.com/diamondbank" },
    { icon: <FaLinkedin />, color: "#0077B5", url: "https://linkedin.com/company/diamondbank" },
    { icon: <FaInstagram />, color: "#E1306C", url: "https://instagram.com/diamondbank" }
  ];

  const footerLinks = [
    "Privacy Policy", "Terms of Service", "Security", "Careers", "Compliance", "Accessibility"
  ];

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Personal Banking", path: "/personal" },
    { name: "Business Banking", path: "/business" },
    { name: "Loans", path: "/loans" },
    { name: "Help Centre", path: "/help-centre" },
    { name: "Contact Us", path: "/contact" }
  ];

  // Updated branch details for Zimbabwe
  const branchDetails = {
    name: "Head Office - Harare",
    address: "80 Nelson Mandela Avenue, Harare, Zimbabwe",
    phone: "+27 76 381 4178",
    email: "info@diamondbank.co.zw",
    hours: "Mon-Fri: 8:30AM - 4:30PM",
    satHours: "Sat: 9:00AM - 1:00PM"
  };

  return (
    <footer style={{
      backgroundColor: colors.primaryDark,
      color: colors.white,
      padding: "70px 20px 40px",
      fontFamily: "'Poppins', sans-serif",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Animated decorative accent bar */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "3px",
        background: `linear-gradient(90deg, ${colors.accent}, ${colors.secondary}, ${colors.accent})`,
        backgroundSize: "200% 100%",
        animation: "gradientMove 3s ease infinite"
      }}></div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "40px",
        maxWidth: "1200px",
        margin: "0 auto",
        position: "relative",
        zIndex: 1
      }}>

        {/* Bank Information Section */}
        <div>
          <div style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "25px"
          }}>
            <img
              src="/images/top.png"
              alt="DiamondBank Logo"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "12px",
                marginRight: "15px",
                objectFit: "cover",
                boxShadow: `0 4px 12px rgba(0,0,0,0.3)`
              }}
            />
            <div>
              <h2 style={{
                fontSize: "24px",
                fontWeight: "700",
                margin: 0,
                letterSpacing: "0.5px"
              }}>
                <span style={{ color: colors.white }}>Diamond</span>
                <span style={{ color: colors.accent }}>Bank</span>
                <span style={{ fontSize: "12px", color: colors.grayLight, display: "block", fontWeight: "400" }}>Zimbabwe</span>
              </h2>
            </div>
          </div>

          <p style={{
            marginBottom: "25px",
            lineHeight: "1.7",
            fontSize: "14px",
            opacity: "0.85",
            color: colors.secondary
          }}>
            Empowering your financial future in Zimbabwe with innovative banking solutions,
            exceptional service, and unwavering commitment to excellence.
          </p>

          {/* Banking Features */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "12px",
            marginTop: "25px"
          }}>
            {[
              { icon: <FaShieldAlt />, text: "Secure Banking" },
              { icon: <FaUsers />, text: "24/7 Support" },
              { icon: <FaBriefcase />, text: "Wealth Management" },
              { icon: <FaGem />, text: "Premium Services" }
            ].map((feature, index) => (
              <div key={index} style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "13px",
                opacity: "0.85"
              }}>
                <div style={{
                  color: colors.accent,
                  fontSize: "14px"
                }}>
                  {feature.icon}
                </div>
                <span style={{ color: colors.secondary }}>{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div style={{
            display: "flex",
            gap: "12px",
            marginTop: "30px"
          }}>
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: social.color,
                  fontSize: "18px",
                  transition: "all 0.3s ease",
                  textDecoration: "none"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.accent;
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.color = colors.white;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.color = social.color;
                }}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links Section */}
        <div>
          <h3 style={{
            color: colors.accent,
            marginBottom: "25px",
            fontSize: "18px",
            fontWeight: "600",
            position: "relative",
            paddingBottom: "12px",
            letterSpacing: "0.5px"
          }}>
            <span style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "40px",
              height: "3px",
              background: colors.accent,
              borderRadius: "3px"
            }}></span>
            Quick Links
          </h3>
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px"
          }}>
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.path}
                style={{
                  color: colors.secondary,
                  textDecoration: "none",
                  fontSize: "14px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.accent;
                  e.currentTarget.style.transform = "translateX(5px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.secondary;
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <FaArrowRight style={{ fontSize: "10px", opacity: 0.6 }} />
                {link.name}
              </a>
            ))}
          </div>
        </div>

        {/* Contact Info Section - Updated with Zimbabwe details */}
        <div>
          <h3 style={{
            color: colors.accent,
            marginBottom: "25px",
            fontSize: "18px",
            fontWeight: "600",
            position: "relative",
            paddingBottom: "12px",
            letterSpacing: "0.5px"
          }}>
            <span style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "40px",
              height: "3px",
              background: colors.accent,
              borderRadius: "3px"
            }}></span>
            Get In Touch
          </h3>

          <div style={{
            display: "grid",
            gap: "18px"
          }}>
            {[
              {
                icon: <FaMapMarkerAlt style={{ color: colors.accent, fontSize: "18px" }} />,
                title: branchDetails.name,
                content: branchDetails.address
              },
              {
                icon: <FaPhone style={{ color: colors.accent, fontSize: "18px" }} />,
                content: branchDetails.phone,
                action: `tel:${branchDetails.phone.replace(/[^0-9+]/g, '')}`
              },
              {
                icon: <FaEnvelope style={{ color: colors.accent, fontSize: "18px" }} />,
                content: branchDetails.email,
                action: `mailto:${branchDetails.email}`
              },
              {
                icon: <FaClock style={{ color: colors.accent, fontSize: "18px" }} />,
                content: branchDetails.hours,
                subContent: branchDetails.satHours
              }
            ].map((item, index) => (
              <div key={index} style={{
                display: "flex",
                gap: "15px",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(107,33,165,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  {item.icon}
                </div>
                <div>
                  {item.title && <p style={{
                    margin: "0 0 4px 0",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: colors.white
                  }}>{item.title}</p>}
                  {item.action ? (
                    <a
                      href={item.action}
                      style={{
                        margin: 0,
                        opacity: "0.85",
                        fontSize: "13px",
                        lineHeight: "1.5",
                        color: colors.secondary,
                        textDecoration: "none",
                        transition: "color 0.3s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = colors.accent}
                      onMouseLeave={(e) => e.currentTarget.style.color = colors.secondary}
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p style={{
                      margin: 0,
                      opacity: "0.85",
                      fontSize: "13px",
                      lineHeight: "1.5",
                      color: colors.secondary
                    }}>{item.content}</p>
                  )}
                  {item.subContent && (
                    <p style={{
                      margin: "4px 0 0 0",
                      opacity: "0.7",
                      fontSize: "11px",
                      color: colors.grayLight
                    }}>{item.subContent}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Google Map Section - Updated with Harare location */}
        <div style={{
          gridColumn: "span 3",
          marginTop: "20px"
        }}>
          <h3 style={{
            color: colors.accent,
            marginBottom: "25px",
            fontSize: "18px",
            fontWeight: "600",
            position: "relative",
            paddingBottom: "12px",
            letterSpacing: "0.5px"
          }}>
            <span style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "40px",
              height: "3px",
              background: colors.accent,
              borderRadius: "3px"
            }}></span>
            Visit Our Head Office in Harare
          </h3>

          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "30px",
            alignItems: "start"
          }}>
            {/* Map - Updated with Harare coordinates */}
            <div style={{
              width: "100%",
              height: "280px",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              position: "relative",
              border: `1px solid ${colors.accent}33`
            }}>
              <iframe
                title="DiamondBank Zimbabwe Harare Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3799.765432109876!2d31.045678901234567!3d-17.812345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a123456789ab%3A0x123456789abcdef!2s80%20Nelson%20Mandela%20Avenue%2C%20Harare%2C%20Zimbabwe!5e0!3m2!1sen!2ssz!4v1692198123456!5m2!1sen!2ssz"
                width="100%"
                height="100%"
                style={{ border: "none", filter: "grayscale(20%)" }}
                allowFullScreen=""
                loading="lazy"
                title="DiamondBank Zimbabwe Location Map"
              ></iframe>
              <div style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                padding: "12px",
                background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
                color: colors.white,
                fontSize: "13px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FaMapMarkerAlt style={{ color: colors.accent, fontSize: "12px" }} />
                    {branchDetails.name}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FaWheelchair style={{ color: colors.accent, fontSize: "12px" }} />
                    Fully Accessible
                  </span>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "15px"
            }}>
              {[
                { icon: "✓", text: "Free Parking Available" },
                { icon: "✓", text: "24/7 ATM on site" },
                { icon: "✓", text: "Wheelchair Accessible" },
                { icon: "✓", text: "Free Wi-Fi" },
                { icon: "✓", text: "Secure Vault" },
                { icon: "✓", text: "Business Centre" }
              ].map((item, index) => (
                <div key={index} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "8px",
                  transition: "all 0.3s ease"
                }}>
                  <span style={{
                    color: colors.accent,
                    fontSize: "16px",
                    fontWeight: "bold"
                  }}>{item.icon}</span>
                  <span style={{
                    fontSize: "12px",
                    color: colors.secondary
                  }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom section - Updated for Zimbabwe */}
      <div style={{
        maxWidth: "1200px",
        margin: "50px auto 0",
        paddingTop: "30px",
        borderTop: `1px solid rgba(255,255,255,0.08)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "25px",
          marginBottom: "25px"
        }}>
          {footerLinks.map((item, index) => (
            <a
              key={index}
              href={`/${item.toLowerCase().replace(/ /g, '-')}`}
              style={{
                color: colors.secondary,
                textDecoration: "none",
                fontSize: "13px",
                fontWeight: "500",
                position: "relative",
                padding: "5px 0",
                transition: "color 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colors.secondary;
              }}
            >
              {item}
            </a>
          ))}
        </div>

        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginBottom: "20px"
        }}>
          <div style={{
            width: "80px",
            height: "1px",
            background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`
          }}></div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <img
              src="/images/top.png"
              alt="DiamondBank Logo"
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "6px",
                objectFit: "cover"
              }}
            />
            <span style={{
              opacity: "0.7",
              fontSize: "13px",
              color: colors.secondary
            }}>
              © {new Date().getFullYear()} DiamondBank Zimbabwe. All rights reserved.
            </span>
          </div>
          <div style={{
            width: "80px",
            height: "1px",
            background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`
          }}></div>
        </div>

        <p style={{
          margin: 0,
          opacity: "0.5",
          fontSize: "11px",
          textAlign: "center",
          maxWidth: "600px",
          color: colors.grayLight
        }}>
          DiamondBank Zimbabwe is licensed and regulated by the Reserve Bank of Zimbabwe.
          All deposits are protected up to ZWL 50,000,000 by the Deposit Protection Scheme.
        </p>
      </div>

      {/* CSS animations */}
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </footer>
  );
};

export default Footer;